import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiRequest } from "../api";
import { buildDocumentTimestamp } from "../utils/documentTimestamp";
import BusinessHeader from "../documents/layouts/BusinessHeader";
import ClientBlock from "../documents/layouts/ClientBlock";
import LineItemsTable from "../documents/layouts/LineItemsTable";
import TotalsBox from "../documents/layouts/TotalsBox";
import "../documents/styles/print.css";

function money(x) {
  if (x == null) return "$0.00";
  const n = typeof x === "string" ? Number(x) : x;
  return Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d || "—");
  }
}

function safeNamePart(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[^a-zA-Z0-9_-]/g, "");
}

function getLastName(name) {
  if (!name) return "Client";
  const parts = String(name).trim().split(/\s+/);
  return parts[parts.length - 1] || "Client";
}

function itemUnits(item) {
  const description = String(item?.description || "");
  return Math.max(1, Math.ceil(description.length / 72));
}

function paginateItems(items) {
  const pages = [];
  let page = [];
  let used = 0;
  let capacity = 7;

  for (const item of items) {
    const units = itemUnits(item);
    if (page.length > 0 && used + units > capacity) {
      pages.push(page);
      page = [];
      used = 0;
      capacity = 16;
    }
    page.push(item);
    used += units;
  }

  if (page.length > 0 || pages.length === 0) pages.push(page);
  return pages;
}

function paginateText(value, maxChars = 2200) {
  const text = String(value || "").trim();
  if (!text) return [];

  const pages = [];
  let current = "";

  for (const paragraph of text.split(/\n\s*\n/)) {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    for (const word of words) {
      const separator = current ? " " : "";
      if (current && current.length + separator.length + word.length > maxChars) {
        pages.push(current);
        current = word;
      } else {
        current += `${separator}${word}`;
      }
    }

    if (current && current.length + 2 <= maxChars) {
      current += "\n\n";
    }
  }

  if (current.trim()) pages.push(current.trim());
  return pages;
}

function lineUnits(value) {
  return Math.max(1, Math.ceil(String(value || "").trim().length / 78));
}

function chunkByUnits(items, maxUnits) {
  const chunks = [];
  let chunk = [];
  let used = 0;

  items.forEach((item) => {
    const units = lineUnits(item);
    if (chunk.length > 0 && used + units > maxUnits) {
      chunks.push(chunk);
      chunk = [];
      used = 0;
    }
    chunk.push(item);
    used += units;
  });

  if (chunk.length > 0) chunks.push(chunk);
  return chunks;
}

function paginateDetailBlocks({
  exclusions,
  conditions,
  paymentSchedule,
  paymentNote,
  terms,
}) {
  const blocks = [];
  const listCapacity = 18;

  chunkByUnits(exclusions, listCapacity).forEach((items, index) => {
    blocks.push({
      type: "exclusions",
      items,
      units: 4 + items.reduce((sum, item) => sum + lineUnits(item), 0),
      continued: index > 0,
    });
  });

  chunkByUnits(conditions, listCapacity).forEach((items, index) => {
    blocks.push({
      type: "conditions",
      items,
      units: 4 + items.reduce((sum, item) => sum + lineUnits(item), 0),
      continued: index > 0,
    });
  });

  if (paymentSchedule.length > 0) {
    blocks.push({
      type: "schedule",
      items: paymentSchedule,
      units: 6 + paymentSchedule.length,
    });
  }

  if (paymentNote) {
    blocks.push({
      type: "payment",
      text: paymentNote,
      units: 4 + lineUnits(paymentNote),
    });
  }

  if (terms) {
    blocks.push({
      type: "terms",
      text: terms,
      units: 4 + lineUnits(terms),
    });
  }

  const pages = [];
  let page = [];
  let used = 0;
  const capacity = 46;

  blocks.forEach((block) => {
    if (page.length > 0 && used + block.units > capacity) {
      pages.push(page);
      page = [];
      used = 0;
    }
    page.push(block);
    used += block.units;
  });

  if (page.length > 0) pages.push(page);
  return pages;
}

function EstimatePageFooter({ businessName, estimateNumber, page, totalPages }) {
  return (
    <div className="jt-estimate-page-footer">
      <span>{businessName}</span>
      <span>
        Estimate {estimateNumber} - Page {page} of {totalPages}
      </span>
    </div>
  );
}

function ContinuationHeading({ estimateNumber }) {
  return (
    <div className="jt-estimate-continuation-heading jt-document-section">
      <div>
        <div style={documentHeadingStyle}>Estimate Details</div>
        <div className="jt-estimate-continuation-number">
          Estimate {estimateNumber}
        </div>
      </div>
    </div>
  );
}

export default function EstimatePrint() {
  const { invoiceId } = useParams();
  const [est, setEst] = useState(null);
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadEstimate() {
      setLoading(true);
      setErr("");

      try {
        const [estimateData, profileData] = await Promise.all([
          apiRequest(`/estimates/${invoiceId}`),
          apiRequest("/profile").catch(() => null),
        ]);

        setEst(estimateData);
        setProfile(profileData);
      } catch (e) {
        setErr(String(e?.message || e || "Failed to load estimate."));
      } finally {
        setLoading(false);
      }
    }

    loadEstimate();
  }, [invoiceId]);

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  if (err) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: "#b00020" }}>{err}</p>
        <Link to={`/estimates/${invoiceId}`}>← Back to Estimate</Link>
      </div>
    );
  }

  if (!est) return null;

  const estimateNumber = est.estimate_number || `EST-${invoiceId}`;
  const rawName = est.client?.contact_name
    ? getLastName(est.client.contact_name)
    : est.client?.company_name || "Client";

  document.title = `Estimate-${safeNamePart(estimateNumber)}-${safeNamePart(rawName)}-${buildDocumentTimestamp()}`;

  const items = Array.isArray(est.items) ? est.items : [];
  const itemPages = paginateItems(items);
  const customerNotes = est.customer_notes || est.notes || "";
  const notePages = paginateText(customerNotes);
  const exclusions = Array.isArray(est.extra_exclusions)
    ? est.extra_exclusions.filter(Boolean)
    : [];
  const conditions = Array.isArray(est.extra_conditions)
    ? est.extra_conditions.filter(Boolean)
    : [];
  const paymentSchedule = Array.isArray(est.payment_schedule)
    ? est.payment_schedule
    : [];
  const detailPages = paginateDetailBlocks({
    exclusions,
    conditions,
    paymentSchedule,
    paymentNote: est.document_payment_note || "",
    terms: est.document_terms || "",
  });
  const totalPages = itemPages.length + notePages.length + detailPages.length;

  const businessName =
    profile?.display_name?.trim() ||
    profile?.legal_name?.trim() ||
    "Your Company";

  const businessAddress = [
    profile?.address_line1,
    profile?.address_line2 ? `Suite ${profile.address_line2}` : null,
    [profile?.city, profile?.state, profile?.postal_code]
      .filter(Boolean)
      .join(" "),
  ]
    .filter(Boolean)
    .join("\n");

  const clientName =
    est.client?.company_name || est.client?.contact_name || "Client";

  const clientAddress = [
    est.client?.address || est.client?.address1 || est.client?.street || "",
    est.client?.address2 || est.client?.suite || est.client?.unit || "",
    [
      est.client?.city,
      est.client?.state,
      est.client?.zip || est.client?.postal_code,
    ]
      .filter(Boolean)
      .join(" "),
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div className="jt-print-page">
      <div className="no-print jt-estimate-print-actions">
        <Link
          to={`/estimates/${invoiceId}`}
          style={{ color: "#00c2ff", fontWeight: 800, textDecoration: "none" }}
        >
          ← Back to Estimate
        </Link>
        <button
          onClick={() => window.print()}
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            border: 0,
            background: "#00c2ff",
            color: "#062033",
            cursor: "pointer",
            fontWeight: 900,
          }}
        >
          Print / Save PDF
        </button>
      </div>

      <section className="jt-print-sheet jt-estimate-print-sheet">
        <BusinessHeader
          businessName={businessName}
          businessAddress={businessAddress}
          phone={profile?.phone}
          email={profile?.email}
          website={profile?.website}
          documentTitle="Estimate"
          documentNumber={estimateNumber}
          issueDate={fmtDate(est.issue_date)}
          dueDate={fmtDate(est.due_date)}
          status={est.status || "DRAFT"}
        />

        <ClientBlock
          title="Estimate For"
          clientName={clientName}
          address={clientAddress}
          phone={est.client?.phone}
          email={est.client?.email}
        />

        <LineItemsTable items={itemPages[0]} money={money} />

        {itemPages.length === 1 && (
          <div className="jt-estimate-totals-row jt-document-section">
            <TotalsBox
              subtotal={est.subtotal}
              tax={est.tax_total}
              total={est.total}
              money={money}
            />
          </div>
        )}

        <EstimatePageFooter
          businessName={businessName}
          estimateNumber={estimateNumber}
          page={1}
          totalPages={totalPages}
        />
      </section>

      {itemPages.slice(1).map((pageItems, index) => {
        const pageNumber = index + 2;
        return (
          <section
            className="jt-print-sheet jt-estimate-print-sheet jt-estimate-continuation-page"
            key={pageNumber}
          >
            <ContinuationHeading estimateNumber={estimateNumber} />
            <LineItemsTable items={pageItems} money={money} />
            {index === itemPages.length - 2 && (
              <div className="jt-estimate-totals-row jt-document-section">
                <TotalsBox
                  subtotal={est.subtotal}
                  tax={est.tax_total}
                  total={est.total}
                  money={money}
                />
              </div>
            )}
            <EstimatePageFooter
              businessName={businessName}
              estimateNumber={estimateNumber}
              page={pageNumber}
              totalPages={totalPages}
            />
          </section>
        );
      })}

      {notePages.map((note, index) => {
        const pageNumber = itemPages.length + index + 1;
        return (
          <section
            className="jt-print-sheet jt-estimate-print-sheet jt-estimate-continuation-page jt-estimate-notes-page"
            key={`notes-${pageNumber}`}
          >
            <ContinuationHeading estimateNumber={estimateNumber} />
            <div className="jt-document-section" style={documentBoxStyle}>
              <div style={documentHeadingStyle}>
                Customer Notes{notePages.length > 1 ? ` (${index + 1} of ${notePages.length})` : ""}
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{note}</div>
            </div>
            <EstimatePageFooter
              businessName={businessName}
              estimateNumber={estimateNumber}
              page={pageNumber}
              totalPages={totalPages}
            />
          </section>
        );
      })}

      {detailPages.map((blocks, index) => {
        const pageNumber = itemPages.length + notePages.length + index + 1;
        return (
          <section
            className="jt-print-sheet jt-estimate-print-sheet jt-estimate-continuation-page jt-estimate-final-page"
            key={`details-${pageNumber}`}
          >
            <ContinuationHeading estimateNumber={estimateNumber} />
            <div className="jt-estimate-full-width-stack">
              {blocks.map((block, blockIndex) => {
                if (block.type === "exclusions" || block.type === "conditions") {
                  const title = block.type === "exclusions" ? "Exclusions" : "Conditions";
                  return (
                    <div
                      className="jt-document-section"
                      style={compactDocumentBoxStyle}
                      key={`${block.type}-${blockIndex}`}
                    >
                      <div style={documentHeadingStyle}>
                        {title}{block.continued ? " (continued)" : ""}
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {block.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: 4 }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }

                if (block.type === "schedule") {
                  return (
                    <div
                      className="jt-document-section jt-estimate-payment-schedule"
                      style={compactDocumentBoxStyle}
                      key={`schedule-${blockIndex}`}
                    >
                      <div style={documentHeadingStyle}>Payment Schedule</div>
                      <table className="jt-document-table">
                        <thead>
                          <tr>
                            <th style={scheduleHeaderStyle}>Payment</th>
                            <th style={{ ...scheduleHeaderStyle, textAlign: "right" }}>Percent</th>
                            <th style={{ ...scheduleHeaderStyle, textAlign: "right" }}>Amount</th>
                            <th style={scheduleHeaderStyle}>Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {block.items.map((item, itemIndex) => {
                            const percent = Number(item?.percent || 0);
                            const amount = (Number(est.total || 0) * percent) / 100;
                            return (
                              <tr key={itemIndex}>
                                <td style={scheduleCellStyle}>{item?.label || `Payment ${itemIndex + 1}`}</td>
                                <td style={{ ...scheduleCellStyle, textAlign: "right" }}>{percent}%</td>
                                <td style={{ ...scheduleCellStyle, textAlign: "right" }}>{money(amount)}</td>
                                <td style={scheduleCellStyle}>{item?.due_when || "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                const title = block.type === "payment" ? "Payment Instructions" : "Terms";
                return (
                  <div
                    className="jt-document-section"
                    style={compactDocumentBoxStyle}
                    key={`${block.type}-${blockIndex}`}
                  >
                    <div style={documentHeadingStyle}>{title}</div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{block.text}</div>
                  </div>
                );
              })}
            </div>
            <EstimatePageFooter
              businessName={businessName}
              estimateNumber={estimateNumber}
              page={pageNumber}
              totalPages={totalPages}
            />
          </section>
        );
      })}
    </div>
  );
}

const documentBoxStyle = {
  border: "1px solid #dbe7f3",
  borderRadius: 14,
  padding: 16,
  background: "#fbfdff",
  color: "#405366",
  fontSize: 13,
  lineHeight: 1.5,
};
const documentHeadingStyle = {
  fontSize: 12,
  fontWeight: 900,
  color: "#8a6a00",
  letterSpacing: 0.7,
  textTransform: "uppercase",
  marginBottom: 8,
};

const compactDocumentBoxStyle = {
  ...documentBoxStyle,
  padding: 11,
  fontSize: 12,
  lineHeight: 1.32,
};

const scheduleHeaderStyle = {
  borderBottom: "1px solid #c9d7e5",
  padding: "7px 8px",
  textAlign: "left",
  color: "#405366",
  fontSize: 11,
  textTransform: "uppercase",
};

const scheduleCellStyle = {
  borderBottom: "1px solid #e6edf4",
  padding: "6px 8px",
  verticalAlign: "top",
};
